// ============================================================
// TMR Portal Admin — acesso local (break-glass), rota discreta /acesso-local
// ============================================================
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Wordmark } from '@tmr/design-system';

/** The route this screen answers on. Discreet on purpose: it is a fallback, not a second front door. */
export const LOCAL_LOGIN_PATH = '/acesso-local';

/** True when the browser is sitting on the break-glass route. */
export function isLocalLoginRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.replace(/\/+$/, '') === LOCAL_LOGIN_PATH;
}

/** Props for {@link LocalLoginPage}. */
export interface LocalLoginPageProps {
  /** Signs in with the credentials; rejects with the message to show (05 I3). */
  onSubmit: (credentials: { email: string; password: string; totpCode: string }) => Promise<void>;
}

/**
 * Break-glass sign-in for when Clerk is unavailable (05 I3, F1).
 *
 * Deliberately plain — no SSO, no magic link, no password reset. Those all depend on the identity
 * provider that is, by hypothesis, down. It is also deliberately unlinked from the normal login: an
 * operator reaches it by typing the URL, which keeps it out of the way of the path everyone uses.
 *
 * The backend answers a uniform 401 whichever check failed, so this screen cannot say "wrong code" or
 * "unknown e-mail" — and must not guess, because guessing out loud is what the uniform answer exists
 * to prevent. A 429 is different and does get its own message: waiting is the actual remedy.
 */
export function LocalLoginPage({ onSubmit }: LocalLoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ email: email.trim(), password, totpCode: totpCode.trim() });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível entrar.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={pageStyle}>
      <form onSubmit={(event) => void submit(event)} style={cardStyle}>
        <Wordmark />
        <div>
          <h1 style={titleStyle}>Acesso local</h1>
          <p style={subtitleStyle}>
            Use esta entrada apenas quando o login normal estiver indisponível. É necessário ter uma
            credencial local provisionada e o seu aplicativo autenticador em mãos.
          </p>
        </div>

        {error && (
          <div role="alert" style={errorStyle}>
            {error}
          </div>
        )}

        <label style={labelStyle}>
          E-mail
          <input
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          Senha
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          Código do autenticador
          <input
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            value={totpCode}
            onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 8))}
            style={{ ...inputStyle, fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }}
          />
        </label>

        <button type="submit" disabled={submitting} style={submitStyle}>
          {submitting ? 'Entrando…' : 'Entrar'}
        </button>

        <a href="/" style={backLinkStyle}>
          Voltar para o login normal
        </a>
      </form>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'var(--cream-100)',
};

const cardStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 16,
  width: '100%',
  maxWidth: 380,
  padding: 32,
  background: 'var(--cream-50)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
};

const titleStyle = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 19,
  color: 'var(--ink-900)',
};

const subtitleStyle = {
  margin: '6px 0 0',
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--fg-muted)',
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 6,
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--ink-900)',
};

const inputStyle = {
  padding: '10px 12px',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--cream-50)',
  color: 'var(--ink-900)',
  outline: 'none',
};

const submitStyle = {
  marginTop: 4,
  padding: '11px 16px',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--cream-50)',
  background: 'var(--forest-900)',
  border: 0,
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

const backLinkStyle = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12.5,
  color: 'var(--forest-700)',
  textAlign: 'center' as const,
};

const errorStyle = {
  padding: '10px 12px',
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  color: 'oklch(38% 0.12 28)',
  background: 'oklch(96% 0.03 28)',
  border: '1px solid oklch(80% 0.09 28)',
  borderRadius: 'var(--radius-sm)',
};
